"use client";

import React from "react";
import { ArrowRight, CircleCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  id: 1,
  title: "Professional Landscaping",
  description: "Transform your outdoor spaces with our comprehensive landscaping solutions designed for both aesthetic appeal and sustainability. We ensure your property makes a lasting first impression.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577498/img/professional_landscaping_service.png"
}, {
  id: 2,
  title: "Interior Painting",
  description: "Revitalize your indoor environment with our premium interior painting services. Our expert team delivers flawless finishes, meticulous attention to detail, and minimal disruption to your daily operations.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577498/img/interior_painting_service.png"
}, {
  id: 3,
  title: "Exterior Painting",
  description: "Protect and beautify your property's exterior with high-quality paints and expert application techniques built to withstand the elements and enhance curb appeal.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577498/img/exterior_painting_service.png"
}, {
  id: 4,
  title: "High-Pressure Power Washing",
  description: "Restore the original shine to your driveways, walkways, and building exteriors with our industrial-grade power washing services, effectively removing years of dirt and grime.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577498/img/power_washing_service.png"
}, {
  id: 5,
  title: "Janitorial and Cleaning Services",
  description: "Maintain an immaculate and healthy environment with our comprehensive daily, weekly, or monthly janitorial cleaning packages tailored to your specific facility needs.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577498/img/janitorial_cleaning_service.png"
}];
const subServices = [{
  serviceId: 1,
  name: "Lawn care & maintenance"
}, {
  serviceId: 1,
  name: "Irrigation system installation"
}, {
  serviceId: 1,
  name: "Tree pruning & removal"
}, {
  serviceId: 2,
  name: "Wall & ceiling painting"
}, {
  serviceId: 2,
  name: "Trim & molding detailing"
}, {
  serviceId: 2,
  name: "Cabinet refinishing"
}, {
  serviceId: 3,
  name: "Siding & stucco painting"
}, {
  serviceId: 3,
  name: "Deck & fence staining"
}, {
  serviceId: 3,
  name: "Weatherproofing treatments"
}, {
  serviceId: 4,
  name: "Driveway & sidewalk cleaning"
}, {
  serviceId: 4,
  name: "Building exterior soft wash"
}, {
  serviceId: 4,
  name: "Graffiti & stain removal"
}, {
  serviceId: 5,
  name: "Daily office cleaning"
}, {
  serviceId: 5,
  name: "Hard floor care & carpet cleaning"
}, {
  serviceId: 5,
  name: "Restroom sanitation & restocking"
}];
interface ServiceBlockProps {
  service: (typeof services)[0];
  index: number;
  "data-index": number;
}
function ServiceBlock({
  service,
  index,
  "data-index": dataIndex
}: ServiceBlockProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const isEven = index % 2 === 0;
  const serviceSubServices = subServices.filter(s => s.serviceId === service.id);
  return <div ref={ref} data-index={dataIndex} className={cn("flex flex-col gap-10 lg:gap-20 items-center transition-all duration-1000 ease-out", isEven ? "lg:flex-row" : "lg:flex-row-reverse", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16")}>
      {}
      <div className="w-full lg:w-1/2">
        <div className="relative group rounded-3xl overflow-hidden shadow-2xl bg-card/40 backdrop-blur-md border border-border/50">
          <img src={service.image} alt={service.title} className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
          <div className="absolute inset-0 border-4 border-primary/5 rounded-3xl pointer-events-none transition-colors duration-500 group-hover:border-primary/20"></div>
        </div>
      </div>

      {}
      <div className={cn("w-full lg:w-1/2 flex flex-col justify-center", isEven ? "border-l-4 border-primary/60 pl-6 lg:pl-12" : "border-l-4 lg:border-l-0 lg:border-r-4 border-primary/60 pl-6 lg:pl-0 lg:pr-12")}>
        <h3 className="text-3xl md:text-4xl font-bold text-foreground mb-6 tracking-tight">
          {service.title}
        </h3>
        <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
          {service.description}
        </p>

        <ul className="space-y-4 mb-10">
          {serviceSubServices.map((sub, i) => <li key={i} data-index={i} className={cn("flex items-center gap-3 text-foreground font-medium", !isEven && "lg:justify-end")}>
              {isEven ? <>
                  <CircleCheck className="w-6 h-6 text-primary shrink-0" strokeWidth={2} />
                  <span>{sub.name}</span>
                </> : <>
                  <span className="lg:order-1">{sub.name}</span>
                  <CircleCheck className="w-6 h-6 text-primary shrink-0 lg:order-2" strokeWidth={2} />
                </>}
            </li>)}
        </ul>

        <div className={cn("flex", !isEven && "lg:justify-end")}>
          <Button asChild size="lg" className="rounded-full px-8 h-14 text-base shadow-lg hover:shadow-xl transition-all shadow-[inset_0_1px_2px_rgba(255,255,255,0.3)]">
            <Link to="https://elyonmaintenance.com/#get-your-maintenance-quote">
              Book This Service
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </Button>
        </div>
      </div>
    </div>;
}
export default function FacilitySolutionsSuite() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.5
  });
  return <section id="facility-solutions-suite" className="relative bg-background py-24 md:py-32 overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full overflow-hidden leading-[0] rotate-180 pointer-events-none">
        <svg className="relative block w-[calc(100%+1.3px)] h-12 md:h-24" viewBox="0 0 1200 120" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="fill-muted/20"></path>
        </svg>
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        {}
        <div ref={headerRef} className={cn("text-center max-w-3xl mx-auto mb-20 md:mb-32 transition-all duration-700 ease-out", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 tracking-tight">
            Facility Solutions Suite
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
            A deep dive into Elyon's specialized maintenance capabilities for residential and commercial clients. We provide a seamless, one-stop experience for all your property care needs.
          </p>
        </div>

        {}
        <div className="space-y-32 md:space-y-48">
          {services.map((service, index) => <ServiceBlock key={service.id} service={service} index={index} data-index={index} />)}
        </div>
      </div>
    </section>;
}